// 公開ホーム(未ログイン)のフォーム入力・テンプレ選択をリードとしてサーバー記録し、
// ログイン/新規登録後に user_uid へ紐付けるためのクライアント。
// 設計の正本は docs/requirements/public-home-lead-logging-plan.md。
//
// - 匿名IDは localStorage にパートナー単位で永続化する (ブランド間で共有しない)。
// - 記録は fire-and-forget。失敗しても UI/操作には一切影響させない。
// - 未ログイン時のみ送信する (トークンがあれば no-op)。
import { apiClient, API_BASE_URL, getStoredToken } from '../../services/apiClient';
import { brandConfig } from '../../config/brands';

export type LeadEventType = 'url_input' | 'template_selected' | 'login_prompt';
type LeadPayload = Record<string, string>;

const ANON_ID_KEY = `lead_anonymous_id:${brandConfig.id}`;
const LEAD_EVENTS_PATH = '/public/lead-events';

/** この機能が有効か: 公開ホームを持つブランドかつ未ログインのときだけ記録する。 */
function isLoggingEnabled(): boolean {
  if (!brandConfig.features?.publicHome) return false;
  if (typeof window === 'undefined') return false;
  return !getStoredToken();
}

function getStoredAnonymousId(): string | null {
  try {
    return window.localStorage.getItem(ANON_ID_KEY);
  } catch {
    return null;
  }
}

/** 匿名IDを読み出し、無ければ発行して localStorage に保存する。 */
export function getOrCreateAnonymousId(): string | null {
  const existing = getStoredAnonymousId();
  if (existing) return existing;
  try {
    const id = crypto.randomUUID();
    window.localStorage.setItem(ANON_ID_KEY, id);
    return id;
  } catch {
    // private mode 等で発行/保存できない場合は記録を諦める (機能は継続)
    return null;
  }
}

function clearAnonymousId(): void {
  try {
    window.localStorage.removeItem(ANON_ID_KEY);
  } catch {
    /* noop */
  }
}

/** sendBeacon / fetch(keepalive) 用の絶対 URL。相対 base のときは origin を補う。 */
function resolveLeadEventsUrl(): string {
  const base = API_BASE_URL.startsWith('http')
    ? API_BASE_URL
    : // eslint-disable-next-line no-restricted-syntax -- /api は vercel.json の rewrite で origin 直下にあり basename (/app) の対象外
      `${window.location.origin}${API_BASE_URL}`;
  return `${base}${LEAD_EVENTS_PATH}`;
}

/** 通常のイベント記録 (axios)。fire-and-forget。 */
export function logLeadEvent(eventType: LeadEventType, payload: LeadPayload): void {
  if (!isLoggingEnabled()) return;
  const anonymousId = getOrCreateAnonymousId();
  if (!anonymousId) return;
  apiClient
    .post(LEAD_EVENTS_PATH, {
      partner_code: brandConfig.id,
      anonymous_id: anonymousId,
      event_type: eventType,
      payload,
    })
    .catch(() => {
      // 記録失敗は握りつぶす (リード取得は best-effort)
    });
}

/**
 * ページ遷移直前用の記録。直後に hardNavigate すると通常 XHR は中断されるため、
 * sendBeacon (不可時は fetch keepalive) で送る。
 * wire format は text/plain の JSON 文字列に固定 (cross-origin での preflight 回避。
 * サーバーは get_json(force=True) で Content-Type 非依存に parse する)。
 */
export function logLeadEventBeacon(eventType: LeadEventType, payload: LeadPayload): void {
  if (!isLoggingEnabled()) return;
  const anonymousId = getOrCreateAnonymousId();
  if (!anonymousId) return;

  const body = JSON.stringify({
    partner_code: brandConfig.id,
    anonymous_id: anonymousId,
    event_type: eventType,
    payload,
  });
  const url = resolveLeadEventsUrl();

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
      if (navigator.sendBeacon(url, blob)) return;
    }
    void fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body,
    }).catch(() => {});
  } catch {
    /* noop */
  }
}

/**
 * ログイン/新規登録後の紐付け。localStorage に匿名IDがあれば link API を呼び、
 * 成功時に匿名IDを削除する。409 (未プロビジョニング) / ネットワークエラー時は
 * 匿名IDを残し、次回訪問時のリトライに委ねる。
 */
export async function linkLeadEvents(): Promise<void> {
  if (typeof window === 'undefined') return;
  const anonymousId = getStoredAnonymousId();
  if (!anonymousId) return;
  try {
    const res = await apiClient.post<{ linked_count?: number }>('/users/me/lead-events/link', {
      anonymous_id: anonymousId,
      partner_code: brandConfig.id,
    });
    // linked_count=0 は「事前イベントがまだ保存されていない / 後着 login_prompt が
    // これから届く」可能性がある。ここで匿名IDを消すと後着イベントを孤児化する
    // (継承元となる紐付け済み行が存在しないため)。1 件以上紐付いたときだけ削除し、
    // 0 件なら残して次回のホーム着地で再試行 → 後着分を回収する。
    if ((res.data?.linked_count ?? 0) > 0) {
      clearAnonymousId();
    }
  } catch {
    // 未紐付けのまま残し、次回のホーム着地で再試行する
  }
}
